import React, { useState } from 'react';
import { PROJECTS } from '../data';
import { Project } from '../types';

interface ProjectCardProps {
  project: Project;
  onClick: (project: Project) => void;
}

const ProjectCard: React.FC<ProjectCardProps> = ({ project, onClick }) => {
  return (
    <div 
      onClick={() => onClick(project)}
      className="group relative bg-zinc-950/50 hover:bg-zinc-900/50 transition-colors cursor-pointer flex flex-col h-full overflow-hidden"
    >
      <div className="p-4 border-b border-zinc-800 flex justify-between items-center font-mono text-[10px] text-zinc-500">
        <span>PROJECT_{project.id.toUpperCase().replace(/-/g, '_')}</span>
        <span className="group-hover:text-red-600 transition-colors">INIT_OPEN_VIEW_</span>
      </div>
      
      <div className="aspect-video relative overflow-hidden grayscale group-hover:grayscale-0 transition-all duration-500">
        <img src={project.image} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/20 group-hover:bg-transparent transition-colors"></div>
      </div>

      <div className="p-6 flex-1 flex flex-col">
        <h3 className="text-2xl font-black text-white mb-2 tracking-tighter uppercase group-hover:text-red-600 transition-colors">
          {project.title}
        </h3>
        <p className="text-zinc-500 text-sm font-light mb-6 line-clamp-2">
          {project.description}
        </p>
        
        <div className="mt-auto pt-6 border-t border-zinc-800/50 flex flex-wrap gap-2">
          {project.tags.slice(0, 3).map(tag => (
            <span key={tag} className="font-mono text-[9px] uppercase tracking-tighter text-zinc-400 bg-zinc-900 px-2 py-0.5 border border-zinc-800">
              {tag}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

const ProjectGrid: React.FC<{ onSelectProject: (p: Project) => void }> = ({ onSelectProject }) => {
  const [showSuggestion, setShowSuggestion] = useState(false);

  const handleSuggestionClick = () => {
    setShowSuggestion(true);
  };

  /**
   * BORDER LOGIC:
   * 1. Every cell gets a bottom border.
   * 2. md: Add left border, then remove it for children 1, 3, 5 (start of 2-col rows).
   * 3. lg: Reset all to have left border, then remove ONLY for children 1, 4, 7 (start of 3-col rows).
   * This ensures Project 2 (Index 3) gets its border back on desktop.
   */
  const cellClass = "border-b border-zinc-800 md:border-l md:[&:nth-child(2n+1)]:border-l-0 lg:border-l lg:[&:nth-child(3n+1)]:border-l-0";

  return (
    <section id="work" className="max-w-[1600px] mx-auto border-x border-zinc-800">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
        {/* Index 1: Section Header Cell */}
        <div className="p-12 border-b border-zinc-800 flex flex-col justify-center">
          <h2 className="font-mono text-xs text-red-600 uppercase tracking-[0.4em] mb-4">Registry</h2>
          <h3 className="text-5xl font-black text-white tracking-tighter uppercase mb-6 leading-none">The <br />Arsenal</h3>
          <p className="text-zinc-500 font-light max-w-xs text-sm">
            A documentation of open-source projects, community tools, and experimental builds.
          </p>
        </div>

        {/* Indices 2-7: Projects */}
        {PROJECTS.map((project) => (
          <div key={project.id} className={cellClass}>
            <ProjectCard project={project} onClick={onSelectProject} />
          </div>
        ))}
        
        {/* Index 8: Placeholder */}
        <div 
          onClick={handleSuggestionClick}
          className={`${cellClass} p-12 flex items-center justify-center group cursor-pointer hover:bg-zinc-900/30 transition-all`}
        >
          <div className="text-center">
            <div className="text-zinc-800 group-hover:text-red-600 text-4xl mb-4 transition-colors">
              <i className="fa-solid fa-plus"></i>
            </div>
            <div className="font-mono text-[10px] uppercase text-zinc-600 group-hover:text-white tracking-widest transition-colors">
              Add_New_Module
            </div>
          </div>
        </div>
      </div>

      {/* Suggestion Modal */}
      {showSuggestion && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-6 bg-black/80 backdrop-blur-sm animate-in fade-in duration-300">
          <div className="max-w-md w-full bg-zinc-950 border border-zinc-800 p-8 shadow-2xl animate-in zoom-in duration-200">
            <div className="flex justify-between items-start mb-6">
              <div className="font-mono text-[10px] text-red-600 uppercase tracking-[0.3em] flex items-center gap-2">
                <i className="fa-solid fa-terminal"></i>
                Incoming_Request
              </div>
              <button 
                onClick={() => setShowSuggestion(false)}
                className="text-zinc-600 hover:text-white transition-colors bg-transparent border-none cursor-pointer p-1"
              >
                <i className="fa-solid fa-xmark"></i>
              </button>
            </div>
            
            <p className="text-white text-xl font-bold tracking-tight mb-8">
              U missing something? <br />
              <span className="text-zinc-400 font-light">Write me a Message with your Ideas and Needings for a Tool.</span>
            </p>

            <button 
              onClick={() => {
                setShowSuggestion(false);
                document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="w-full py-4 bg-red-600 text-white font-mono text-[10px] uppercase font-bold tracking-[0.2em] hover:bg-red-700 transition-colors border-none cursor-pointer"
            >
              Open_Channel_Now
            </button>
          </div>
        </div>
      )}
    </section>
  );
};

export default ProjectGrid;